import type { AssetManager } from "../../assets/asset-manager";
import type { CharacterDefinition } from "../../data/characters";
import type { PerfMonitor } from "../../perf/perf-monitor";
import type { Scene } from "../scene";
import { FlappyEngine } from "./flappy-engine";
import { FlappyInput } from "./flappy-input";
import { FlappyRenderer } from "./flappy-renderer";

export interface FlappySceneCallbacks {
  home: () => void;
}

export class FlappyScene implements Scene {
  private screen: HTMLElement | null = null;
  private engine: FlappyEngine | null = null;
  private input: FlappyInput | null = null;
  private animationFrame = 0;
  private previousTime = 0;
  private deadTimer: number | undefined;
  private renderer: FlappyRenderer | null = null;
  private sprite: HTMLImageElement | undefined;
  private background: HTMLImageElement | undefined;
  private obstacle: HTMLImageElement | undefined;

  constructor(
    private readonly assets: AssetManager,
    private readonly character: CharacterDefinition,
    private readonly bestScoreStorage: Storage,
    private readonly monitor: PerfMonitor,
    private readonly callbacks: FlappySceneCallbacks,
  ) {}

  async mount(root: HTMLElement): Promise<void> {
    const screen = document.createElement("main");
    screen.className = "flappy-screen scene-shell";
    screen.innerHTML = `
      <div class="game-topbar"><button class="game-home-button" type="button">← Home</button><span class="game-label">SKY HOP</span><span class="game-score" aria-live="polite">0</span></div>
      <div class="flappy-stage">
        <canvas class="flappy-canvas" aria-label="Flappy mini game. Tap, click, or press Space to flap."></canvas>
        <div class="game-hint">TAP TO START<br><small>SPACE ALSO FLAPS</small></div>
        <div class="result-overlay" hidden>
          <p class="result-kicker">FLIGHT COMPLETE</p><h1>GAME OVER</h1>
          <div class="result-stats"><span>SCORE <b class="result-score">0</b></span><span>BEST <b class="result-best">0</b></span></div>
          <div class="result-actions"><button class="retry-button" type="button">RETRY</button><button class="result-home-button" type="button">HOME</button></div>
        </div>
      </div>
    `;
    root.replaceChildren(screen);
    this.screen = screen;
    const canvas = screen.querySelector<HTMLCanvasElement>(".flappy-canvas")!;
    const best = Number(this.bestScoreStorage.getItem("bestScore") ?? 0) || 0;
    this.engine = new FlappyEngine(best);
    this.renderer = new FlappyRenderer(canvas);
    this.sprite = await this.assets.loadImage(this.character.game.sprite).catch(() => undefined);
    this.background = await this.assets.loadImage("/assets/flappy/background.svg").catch(() => undefined);
    this.obstacle = await this.assets.loadImage("/assets/flappy/obstacle.svg").catch(() => undefined);
    this.input = new FlappyInput(canvas, () => this.engine?.flap());
    this.input.attach();
    screen.querySelector<HTMLButtonElement>(".game-home-button")!.addEventListener("click", this.callbacks.home);
    screen.querySelector<HTMLButtonElement>(".result-home-button")!.addEventListener("click", this.callbacks.home);
    screen.querySelector<HTMLButtonElement>(".retry-button")!.addEventListener("click", () => this.retry());
    this.loop(0);
    this.monitor.mark("game-first-frame");
    this.monitor.measure("play-to-game", "play-start", "game-first-frame");
  }

  private loop = (time: number): void => {
    if (!this.engine || !this.renderer) return;
    const delta = this.previousTime === 0 ? 0 : (time - this.previousTime) / 1000;
    this.previousTime = time;
    this.engine.update(delta);
    this.renderer.render(this.engine, this.sprite, this.background, this.obstacle);
    this.updateHud();
    if (this.engine.state === "DEAD" && this.deadTimer === undefined) {
      this.deadTimer = window.setTimeout(() => {
        this.engine?.markResult();
        this.showResult();
      }, 350);
    }
    this.animationFrame = window.requestAnimationFrame(this.loop);
  };

  private updateHud(): void {
    if (!this.screen || !this.engine) return;
    this.screen.querySelector<HTMLElement>(".game-score")!.textContent = String(this.engine.score);
    this.screen.querySelector<HTMLElement>(".game-hint")!.hidden = this.engine.state !== "READY";
  }

  private showResult(): void {
    if (!this.screen || !this.engine) return;
    this.bestScoreStorage.setItem("bestScore", String(this.engine.bestScore));
    this.screen.querySelector<HTMLElement>(".result-score")!.textContent = String(this.engine.score);
    this.screen.querySelector<HTMLElement>(".result-best")!.textContent = String(this.engine.bestScore);
    this.screen.querySelector<HTMLElement>(".result-overlay")!.hidden = false;
  }

  private retry(): void {
    if (!this.engine || !this.screen) return;
    this.engine.reset();
    this.deadTimer = undefined;
    this.screen.querySelector<HTMLElement>(".result-overlay")!.hidden = true;
    this.previousTime = 0;
  }

  unmount(): void {
    if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
    if (this.deadTimer !== undefined) window.clearTimeout(this.deadTimer);
    this.input?.detach();
    this.screen?.remove();
    this.screen = null;
    this.engine = null;
    this.renderer = null;
  }
}
